import { useState, useEffect } from 'react'
import { Menu, X, Shield } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Link, useLocation } from 'wouter'
import { toast } from 'sonner'

const navItems = [
  { href: '#programa', label: 'O Programa' },
  { href: '#cpsi', label: 'CPSI' },
  { href: '#etapas', label: 'Etapas' },
  { href: '#desafios', label: 'Desafios' },
  { href: '#documentos', label: 'Documentos' },
]

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  const { isAuthenticated, logout, admin } = useAuth()
  const [location, navigate] = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60)

      const sections = navItems.map((item) => item.href.replace('#', ''))
      let current = ''
      for (const section of sections) {
        const element = document.getElementById(section)
        if (element && element.getBoundingClientRect().top <= 120) {
          current = section
        }
      }
      setActiveSection(current)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (href: string) => {
    setIsOpen(false)

    const jump = () => {
      const element = document.querySelector(href)
      if (element) {
        const offset = 80
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - offset
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      }
    }

    if (location !== '/') {
      navigate('/')
      window.setTimeout(jump, 150)
      return
    }

    jump()
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
    toast.success('Logout realizado com sucesso!')
  }

  const handleAdminAccess = () => {
    if (isAuthenticated) {
      navigate('/admin/dashboard')
    } else {
      navigate('/admin/login')
    }
    setIsOpen(false)
  }

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-gray-100'
          : 'bg-white shadow-sm'
      }`}
      role="navigation"
      aria-label="Navegação principal"
    >
      <div className="container flex items-center justify-between py-3">
        <a
          href="#programa"
          onClick={(e) => {
            e.preventDefault()
            scrollToSection('#programa')
          }}
          className="flex items-center gap-2 group"
          aria-label="Voltar ao topo - Catalisa GOV Porto Velho"
        >
          <div className="w-9 h-9 bg-[#0d6b3d] rounded-lg flex items-center justify-center text-white font-bold text-sm font-serif shadow-sm">
            CG
          </div>
          <div className="hidden sm:block">
            <div className="font-serif font-bold text-[#0d6b3d] text-sm leading-tight">
              Catalisa GOV
            </div>
            <div className="text-[10px] text-gray-500 leading-tight">Porto Velho</div>
          </div>
        </a>

        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => {
                e.preventDefault()
                scrollToSection(item.href)
              }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeSection === item.href.replace('#', '')
                  ? 'text-[#0d6b3d] bg-green-50'
                  : 'text-gray-600 hover:text-[#0d6b3d] hover:bg-gray-50'
              }`}
            >
              {item.label}
            </a>
          ))}
          
          <button
            onClick={handleAdminAccess}
            className={`ml-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
              isAuthenticated
                ? 'text-[#0d6b3d] bg-green-50 hover:bg-green-100'
                : 'text-gray-600 hover:text-[#0d6b3d] hover:bg-gray-50'
            }`}
          >
            <Shield size={16} />
            {isAuthenticated ? 'Admin' : 'Área Restrita'}
          </button>

          {isAuthenticated && admin && (
            <button
              onClick={handleLogout}
              className="ml-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
            >
              Sair
            </button>
          )}

          <Link
            href="/consulta-publica"
            className="ml-2 px-4 py-2 bg-[#0d6b3d] text-white text-sm font-semibold rounded-lg hover:bg-[#0b5b35] transition-colors shadow-sm"
          >
            Participe
          </Link>
        </div>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors"
          aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {isOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white pb-4">
          <div className="container flex flex-col gap-1 pt-2">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault()
                  scrollToSection(item.href)
                }}
                className={`px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  activeSection === item.href.replace('#', '')
                    ? 'text-[#0d6b3d] bg-green-50'
                    : 'text-gray-600 hover:text-[#0d6b3d] hover:bg-gray-50'
                }`}
              >
                {item.label}
              </a>
            ))}
            
            <button
              onClick={handleAdminAccess}
              className={`px-3 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                isAuthenticated
                  ? 'text-[#0d6b3d] bg-green-50'
                  : 'text-gray-600 hover:text-[#0d6b3d] hover:bg-gray-50'
              }`}
            >
              <Shield size={18} />
              {isAuthenticated ? 'Painel Admin' : 'Área Restrita'}
            </button>

            {isAuthenticated && admin && (
              <button
                onClick={handleLogout}
                className="px-3 py-2.5 rounded-md text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors text-left"
              >
                Sair
              </button>
            )}

            <Link
              href="/consulta-publica"
              onClick={() => setIsOpen(false)}
              className="mt-2 px-4 py-2.5 bg-[#0d6b3d] text-white text-sm font-semibold rounded-lg hover:bg-[#0b5b35] transition-colors text-center"
            >
              Participe da Consulta Pública
            </Link>
          </div>
        </div>
      )}
    </nav>
  )
}